import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { LuClipboardPaste } from "react-icons/lu";
// eslint-disable-next-line no-unused-vars
import { appendMatcherNodeToPipeline, editMatcherNode } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './MatcherConfigModal.css';

// Set the app element for accessibility
Modal.setAppElement('#root');

const UNSPECIFIED_CONS = 'Consumption column not specified';
const UNSPECIFIED_GEN = 'Generation column not specified';

const normalizeOptionalColumn = (value, sentinelLabel = null) => {
  if (value === undefined || value === null) {
    return '';
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return '';
  }
  if (sentinelLabel && trimmed.toLowerCase() === sentinelLabel.toLowerCase()) {
    return '';
  }
  return String(value);
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999, // Higher z-index to ensure it appears on top
  }
};

const MatcherConfigModal = ({ isOpen, onClose, onConfigured, isReconfiguration = false, initialData = {} }) => {
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [cons_col_name, setCons_col_name] = useState(
    normalizeOptionalColumn(initialData.cons_col_name, UNSPECIFIED_CONS)
  );
  const [gen_col_name, setGen_col_name] = useState(
    normalizeOptionalColumn(initialData.gen_col_name, UNSPECIFIED_GEN)
  );
  const { session } = usePipeline(); // Use the context to get the session
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Determine if we're in edit mode (has a name AND isConfigured is true but not reconfiguring)
  const isEditing = !!initialData.name && initialData.isConfigured === true && !isReconfiguration;
  
  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);

  // Keep local form state in sync on each modal open.
  useEffect(() => {
    if (!isOpen) return;
    setNodeName(initialData.name || '');
    setCons_col_name(normalizeOptionalColumn(initialData.cons_col_name, UNSPECIFIED_CONS));
    setGen_col_name(normalizeOptionalColumn(initialData.gen_col_name, UNSPECIFIED_GEN));
    setSubmitError(null);
  }, [isOpen, initialData.name, initialData.cons_col_name, initialData.gen_col_name]);
  
  // Function to paste from clipboard for consumption column
  const pasteFromClipboardCons = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setCons_col_name(text);
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  // Function to paste from clipboard for generation column
  const pasteFromClipboardGen = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setGen_col_name(text);
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        // No need for alert as the button will be disabled
        return;
      }

      const normalizedCons = normalizeOptionalColumn(cons_col_name, UNSPECIFIED_CONS);
      const normalizedGen = normalizeOptionalColumn(gen_col_name, UNSPECIFIED_GEN);
      
      // Get the input node names and position from initialData
      const inputNodes = initialData.inputNodes || {};
      const position = initialData.position || { x: 0, y: 0 };
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      // Log the configuration mode
      console.log("Configuration mode:", {
        isEditing,
        isReconfiguration,
        name: initialData.name,
        isConfigured: initialData.isConfigured
      });
      
      // Make the API call if we have a valid pipeline name
      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          if (isEditing) {
            // We're editing an existing node
            console.log(`Calling API to edit matcher node: ${initialData.name} in pipeline: ${session.pipeline_name}`);
            console.log(`New consumption column name: ${normalizedCons}, New generation column name: ${normalizedGen}`);
            
            // In edit mode, we only update the column names
            await editMatcherNode(
              session.pipeline_name,
              initialData.name,  // Use the original name
              normalizedCons || null,
              normalizedGen || null
            );
            
            console.log(`Successfully edited matcher node ${initialData.name}`);
          } else if (!isReconfiguration && inputNodes.cons_input && inputNodes.gen_input) {
            // We're creating a new node
            console.log(`Calling API to append matcher node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Consumption node: ${inputNodes.cons_input}, Generation node: ${inputNodes.gen_input}`);
            console.log(`Consumption Column: ${normalizedCons}, Generation Column: ${normalizedGen}`);
            console.log(`Position: (${position.x}, ${position.y})`);
            
            await appendMatcherNodeToPipeline(
              session.pipeline_name,
              nodeName,
              normalizedCons || '',
              normalizedGen || '',
              inputNodes.cons_input,
              inputNodes.gen_input,
              position.x,
              position.y
            );
            
            console.log(`Successfully added matcher node ${nodeName} to backend`);
          } else if (isReconfiguration) {
            console.log(`Skipping API call for node ${nodeName} as it's being reconfigured by parent component`);
          }
        } catch (apiError) {
          console.error(`Error calling ${isEditing ? 'editMatcherNode' : 'appendMatcherNodeToPipeline'}:`, apiError);
          const info = notifyError(apiError, {
            operation: isEditing ? OP.node.editMatcher : OP.node.appendMatcher,
            showToast: false,
          });
          setSubmitError(info);
          // Don't continue with UI updates if API call fails
          setIsSubmitting(false);
          return;
        }
      } else {
        console.log(`Skipping API call for unsaved pipeline`);
      }
      
      // Call the callback to update the UI
      onConfigured({
        name: isEditing ? initialData.name : nodeName, // Keep original name in edit mode
        cons_col_name: normalizedCons,
        gen_col_name: normalizedGen
      });
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring matcher node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
      return; // Exit early on error
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Matcher Configuration"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false} // Prevents warning about appElement
    >
      <div className="matcher-modal">
        <button onClick={onClose} className="matcher-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="matcher-modal-title">
          {isReconfiguration ? "Reconfigure Matcher Node" : 
           isEditing ? "Edit Matcher Parameters" : 
           "Configure New Matcher Node"}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="matcher-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <div className="matcher-modal-field">
          <label className="matcher-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={nodeName}
              className="matcher-modal-input"
              disabled={true}
              title="Node name cannot be changed in edit mode"
            />
          ) : (
            <input
              type="text"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              onBlur={() => {
                // Validate again on blur to ensure we show errors when user leaves the field
                const validation = validateNodeName(nodeName);
                setNodeNameError(validation.isValid ? '' : validation.errorMessage);
              }}
              className={`matcher-modal-input ${nodeNameError ? 'error' : ''}`}
              placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
              aria-invalid={!!nodeNameError}
              aria-describedby={nodeNameError ? "node-name-error" : undefined}
            />
          )}
          {!isEditing && nodeNameError && (
            <div className="matcher-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!isEditing && !nodeNameError && (
            <div className="matcher-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
        </div>
        
        <div className="matcher-modal-field">
          <label className="matcher-modal-label">
            Consumption Column Name:
          </label>
          <div className="input-with-icon">
            <input
              type="text"
              value={cons_col_name}
              onChange={(e) => setCons_col_name(e.target.value)}
              className="matcher-modal-input"
              placeholder="Leave blank to auto-select last output column from consumption input"
              title="Optional: leave blank to auto-select the last output column from the consumption input node"
            />
            <button 
              type="button"
              onClick={pasteFromClipboardCons}
              className="paste-button"
              title="Paste column name from clipboard"
            >
              <LuClipboardPaste size={18} />
            </button>
          </div>
        </div>

        <div className="matcher-modal-field">
          <label className="matcher-modal-label">
            Generation Column Name:
          </label>
          <div className="input-with-icon">
            <input
              type="text"
              value={gen_col_name}
              onChange={(e) => setGen_col_name(e.target.value)}
              className="matcher-modal-input"
              placeholder="Leave blank to auto-select last output column from generation input"
              title="Optional: leave blank to auto-select the last output column from the generation input node"
            />
            <button 
              type="button"
              onClick={pasteFromClipboardGen}
              className="paste-button"
              title="Paste column name from clipboard"
            >
              <LuClipboardPaste size={18} />
            </button>
          </div>
        </div>
        
        <div className="matcher-modal-button-container">
          <button 
            className="matcher-modal-configure-button"
            onClick={handleConfigure}
            disabled={
              isSubmitting || 
              (!isEditing && (!!nodeNameError || !nodeName))
            }
            title={
              nodeNameError ? nodeNameError : 
              (!nodeName && !isEditing) ? 'Node name is required' : ''
            }
          >
            {isSubmitting ? 'Processing...' : 
              isReconfiguration ? 'Reconfigure' : 
              isEditing ? 'Save Changes' : 
              'Configure'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MatcherConfigModal;